import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { 
  ImageIcon, 
  Download, 
  RefreshCw, 
  Loader2,
  BarChart3,
  Network,
  Calendar,
  AlertTriangle,
  Code2,
  Users,
  GitBranch
} from 'lucide-react'

interface VisualizationPanelProps {
  projectId: string
  projectName?: string
  isOpen: boolean
  onClose: () => void
}

interface Diagram {
  type: string
  path: string
  filename: string
  format?: 'png' | 'svg' | 'html'
}

type LoadedDiagram =
  | { kind: 'html'; html: string }
  | { kind: 'blob'; url: string; mimeType: string }

const DEFAULT_API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000'
const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL

const getAccessToken = () => localStorage.getItem('access_token')

const getAuthHeaders = (): HeadersInit => {
  const token = getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const getDiagramKindFromFilename = (filename: string): 'html' | 'blob' => {
  const lower = (filename || '').toLowerCase()
  if (lower.endsWith('.html')) return 'html'
  return 'blob'
}

export function VisualizationPanel({ projectId, projectName, isOpen, onClose }: VisualizationPanelProps) {
  const [diagrams, setDiagrams] = useState<Diagram[]>([])
  const [diagramUrls, setDiagramUrls] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [loadedDiagrams, setLoadedDiagrams] = useState<Record<string, LoadedDiagram>>({})

  useEffect(() => {
    if (isOpen && projectId) {
      loadVisualizations()
    }
  }, [isOpen, projectId])

  useEffect(() => {
    return () => {
      // Cleanup blob URLs on unmount
      Object.values(loadedDiagrams).forEach((item) => {
        if (item.kind === 'blob') {
          URL.revokeObjectURL(item.url)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchDiagramContent = async (diagram: Diagram): Promise<LoadedDiagram | null> => {
    const endpoint = `${API_BASE_URL}/api/project/${projectId}/visualizations/${encodeURIComponent(diagram.filename)}`
    const response = await fetch(endpoint, {
      headers: {
        ...getAuthHeaders(),
      },
    })

    if (!response.ok) {
      return null
    }

    const kind = getDiagramKindFromFilename(diagram.filename)
    if (kind === 'html') {
      const html = await response.text()
      return { kind: 'html', html }
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const mimeType = blob.type || response.headers.get('content-type') || 'application/octet-stream'
    return { kind: 'blob', url, mimeType }
  }

  const ensureDiagramLoaded = async (diagram: Diagram) => {
    if (loadedDiagrams[diagram.filename]) return
    try {
      const loaded = await fetchDiagramContent(diagram)
      if (!loaded) return
      setLoadedDiagrams((prev) => ({ ...prev, [diagram.filename]: loaded }))
    } catch (e) {
      console.error('Failed to fetch diagram content:', e)
    }
  }

  const loadVisualizations = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/project/${projectId}/visualizations`, {
        headers: {
          ...getAuthHeaders(),
        },
      })
      if (response.ok) {
        const data = await response.json()
        const nextDiagrams = (data.diagrams || []) as Diagram[]
        setDiagrams(nextDiagrams)

        // Reset previously loaded diagrams to avoid leaking blob URLs across projects
        setLoadedDiagrams((prev) => {
          Object.values(prev).forEach((item) => {
            if (item.kind === 'blob') URL.revokeObjectURL(item.url)
          })
          return {}
        })

        // Prefetch all diagrams so <img>/<iframe> don’t need auth headers
        await Promise.all(nextDiagrams.map((d) => ensureDiagramLoaded(d)))
      }
    } catch (error) {
      console.error('Failed to load visualizations:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateVisualizations = async () => {
    setGenerating(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(
        `${API_BASE_URL}/api/project/${projectId}/visualizations/generate`,
        { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
        }
      )
      if (response.ok) {
        const data = await response.json()
        const nextDiagrams = (data.diagrams || []) as Diagram[]
        setDiagrams(nextDiagrams)

        setLoadedDiagrams((prev) => {
          Object.values(prev).forEach((item) => {
            if (item.kind === 'blob') URL.revokeObjectURL(item.url)
          })
          return {}
        })

        await Promise.all(nextDiagrams.map((d) => ensureDiagramLoaded(d)))
        setActiveTab('architecture')
      } else {
        const error = await response.json()
        console.error('Visualization generation failed:', error)
      }
    } catch (error) {
      console.error('Failed to generate visualizations:', error)
    } finally {
      setGenerating(false)
    }
  }

  const downloadDiagram = (diagram: Diagram) => {
    const loaded = loadedDiagrams[diagram.filename]

    // Ensure we have it loaded before download
    if (!loaded) {
      void (async () => {
        await ensureDiagramLoaded(diagram)
        downloadDiagram(diagram)
      })()
      return
    }

    if (loaded.kind === 'blob') {
      const link = document.createElement('a')
      link.href = loaded.url
      link.download = diagram.filename
      link.click()
      return
    }

    const blob = new Blob([loaded.html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = diagram.filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const exportAllVisualizations = async () => {
    try {
      // Fetch all HTML visualizations
      const htmlDiagrams = diagrams.filter(d => d.filename.endsWith('.html'))
      
      if (htmlDiagrams.length === 0) {
        alert('No interactive visualizations to export')
        return
      }

      // Deduplicate by type - keep only the first occurrence of each type
      const uniqueDiagrams = htmlDiagrams.reduce((acc, diagram) => {
        if (!acc.find(d => d.type === diagram.type)) {
          acc.push(diagram)
        }
        return acc
      }, [] as typeof htmlDiagrams)

      // Fetch content of each visualization and extract body content only
      const contents = await Promise.all(
        uniqueDiagrams.map(async (diagram, index) => {
          console.log(`[Export] Processing diagram ${index}:`, diagram.type, diagram.filename)
          let html: string | null = null
          const loaded = loadedDiagrams[diagram.filename]
          if (loaded && loaded.kind === 'html') {
            html = loaded.html
          } else {
            const fetched = await fetchDiagramContent(diagram)
            if (fetched && fetched.kind === 'html') {
              html = fetched.html
              setLoadedDiagrams((prev) => ({ ...prev, [diagram.filename]: fetched }))
            }
          }

          if (!html) {
            console.warn(`[Export] No HTML for ${diagram.type}`)
            return {
              title: getDiagramTitle(diagram.type),
              content: '<div>Failed to load visualization</div>',
              styles: '',
              scripts: '',
              type: diagram.type,
            }
          }
          
          console.log(`[Export] Successfully loaded ${diagram.type}, HTML length:`, html.length)
          
          // Extract the full HTML to preserve styles and structure
          // Parse to get body content and styles separately
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')
          
          // Extract all styles from the original document
          const styles = Array.from(doc.querySelectorAll('style'))
            .map(style => style.textContent)
            .join('\n')
          
          // Extract all scripts (excluding external library scripts)
          const scripts = Array.from(doc.querySelectorAll('script'))
            .filter(script => !script.src) // Only inline scripts
            .map(script => script.textContent)
            .join('\n')
          
          // Extract body content WITHOUT script tags to avoid double rendering
          const bodyClone = doc.body?.cloneNode(true) as HTMLElement
          if (bodyClone) {
            // Remove all script tags from the body clone
            bodyClone.querySelectorAll('script').forEach(script => script.remove())
            
            // Update onclick handlers to use section-specific function names
            // Only transform showTab function calls, leave other functions alone
            bodyClone.querySelectorAll('[onclick]').forEach(element => {
              const onclick = element.getAttribute('onclick')
              if (onclick && onclick.includes('showTab')) {
                // Replace showTab function calls with section-specific versions
                element.setAttribute('onclick', onclick.replace(/showTab\(/g, `showTab_section${index}(`))
              }
            })
          }
          const bodyContent = bodyClone?.innerHTML || doc.body?.innerHTML || html
          
          console.log(`[Export] ${diagram.type} - Content length:`, bodyContent.length, 'Scripts length:', scripts.length)
          
          return {
            title: getDiagramTitle(diagram.type),
            content: bodyContent,
            styles: styles,
            scripts: scripts,
            type: diagram.type
          }
        })
      )

      // Create combined HTML document matching original visualization styling
      const combinedHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Project Visualizations - ${projectName || projectId}</title>
    
    <!-- External Libraries -->
    <script src="https://d3js.org/d3.v7.min.js"></script>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
    
    <style>
        /* Global Reset */
        :root {
            --primary-gradient: linear-gradient(135deg, #4A90E2 0%, #2C5AA0 100%);
            --header-gradient: linear-gradient(135deg, #2C3E50 0%, #34495E 100%);
        }
        
        html { scroll-behavior: smooth; }
        
        /* Main Layout */
        body.combined-export {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: white;
            padding: 20px;
            min-height: 100vh;
            margin: 0;
        }
        .main-container {
            max-width: 1600px;
            margin: 0 auto;
        }
        
        /* Header Styling */
        .main-header {
            background: linear-gradient(135deg, #2C3E50 0%, #34495E 100%);
            color: white;
            padding: 40px;
            text-align: center;
            border-radius: 20px 20px 0 0;
            box-shadow: 0 25px 80px rgba(0,0,0,0.25);
        }
        .main-header h1 {
            font-size: 42px;
            margin-bottom: 12px;
            font-weight: 800;
            letter-spacing: -1px;
        }
        .main-header p {
            font-size: 16px;
            opacity: 0.9;
            font-weight: 500;
        }
        
        /* Table of Contents - Horizontal Menu */
        .table-of-contents {
            background: white;
            padding: 20px 30px;
            margin-bottom: 20px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.15);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 15px;
            flex-wrap: wrap;
            border-radius: 0 0 20px 20px;
        }
        .toc-label {
            color: #2C3E50;
            font-size: 18px;
            font-weight: 700;
            margin-right: 10px;
        }
        .toc-item {
            padding: 10px 20px;
            background: linear-gradient(135deg, #4A90E2 0%, #2C5AA0 100%);
            color: white;
            border-radius: 20px;
            cursor: pointer;
            transition: transform 0.2s, box-shadow 0.2s;
            font-weight: 600;
            font-size: 14px;
            white-space: nowrap;
            text-decoration: none;
            display: inline-block;
        }
        .toc-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(74, 144, 226, 0.5);
        }
        
        /* Visualization Sections */
        .visualization-section {
            background: white;
            margin-bottom: 20px;
            border-radius: 20px;
            box-shadow: 0 25px 80px rgba(0,0,0,0.25);
            overflow: hidden;
            scroll-margin-top: 20px;
        }
        .section-header {
            background: white;
            color: #003366;
            padding: 30px 40px;
            font-size: 34px;
            font-weight: 900;
            letter-spacing: -0.5px;
            border-bottom: 3px solid #003366;
        }
        .visualization-content {
            /* Content will have its own styles from original visualization */
            padding-top: 20px;
        }
        
        /* Override internal visualization container styling */
        .visualization-content .container {
            border-radius: 0 !important;
            box-shadow: none !important;
            max-width: none !important;
        }
        
        /* Ensure internal headers match section width */
        .visualization-content .header {
            max-width: none !important;
            width: 100% !important;
            padding: 30px 40px !important;
            margin: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
        }
        
        /* Ensure content sections match width */
        .visualization-content .content {
            max-width: none !important;
            margin: 0 !important;
        }
        
        /* Footer */
        .footer {
            text-align: center;
            color: white;
            padding: 40px;
            font-size: 14px;
            font-weight: 500;
            margin-top: 20px;
        }
        
        /* Print Styles */
        @media print {
            body.combined-export { 
                background: white; 
                padding: 0; 
            }
            .main-header { 
                border-radius: 0; 
                page-break-after: avoid;
            }
            .table-of-contents { 
                display: none; 
            }
            .visualization-section { 
                page-break-after: always;
                box-shadow: none;
                page-break-inside: avoid;
            }
            .visualization-section:last-of-type {
                page-break-after: auto;
            }
        }
        
        /* Individual Visualization Styles - Scoped */
        ${contents.map((item, index) => `
        /* Styles for ${item.title} */
        #section-${index} .visualization-content {
            ${item.styles}
        }
        `).join('\n')}
    </style>
</head>
<body class="combined-export">
    <div class="main-container">
        <div class="main-header">
            <h1>📊 Project Visualizations</h1>
            <p>Project: ${projectName || projectId} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
        </div>
        
        <div class="table-of-contents">
            <span class="toc-label">📑 Quick Navigation:</span>
            ${contents.map((item, index) => `<a href="#section-${index}" class="toc-item">${item.title}</a>`).join('\n            ')}
        </div>
        
        ${contents.map((item, index) => `
        <div class="visualization-section" id="section-${index}">
            <div class="section-header">${item.title}</div>
            <div class="visualization-content">
                ${item.content}
            </div>
        </div>
        `).join('\n')}
        
        <div class="footer">
            <p>Generated by Project Adya | All visualizations are interactive and presentation-ready</p>
        </div>
    </div>
    
    <!-- Inline scripts from visualizations -->
    ${contents.map((item, index) => {
      if (!item.scripts) return '';
      
      // Transform scripts to work within sectioned export
      let modifiedScript = item.scripts;
      
      // 1. Only rename showTab function (used in architecture diagrams)
      modifiedScript = modifiedScript.replace(/function\s+(showTab)\s*\(/g, `function $1_section${index}(`);
      
      // 2. Scope tab-related document queries
      modifiedScript = modifiedScript.replace(
        /document\.querySelectorAll\((['"])\.tab\1\)/g,
        `document.getElementById('section-${index}').querySelectorAll('.tab')`
      );
      modifiedScript = modifiedScript.replace(
        /document\.querySelectorAll\((['"])\.tab-content\1\)/g,
        `document.getElementById('section-${index}').querySelectorAll('.tab-content')`
      );
      
      // 3. Scope D3 selectors to the section (for dataflow and other D3 visualizations)
      // Replace d3.select('body') or d3.select('#content') with section-scoped versions
      modifiedScript = modifiedScript.replace(
        /d3\.select\((['"])#content\1\)/g,
        `d3.select('#section-${index} .content')`
      );
      modifiedScript = modifiedScript.replace(
        /d3\.select\((['"])#flows-container\1\)/g,
        `d3.select('#section-${index} #flows-container')`
      );
      modifiedScript = modifiedScript.replace(
        /d3\.select\((['"])#legend\1\)/g,
        `d3.select('#section-${index} #legend')`
      );
      modifiedScript = modifiedScript.replace(
        /d3\.select\((['"])#tooltip\1\)/g,
        `d3.select('#section-${index} #tooltip')`
      );
      
      // Only wrap in IIFE if it doesn't have showTab (which needs global scope for onclick)
      const hasShowTab = modifiedScript.includes('showTab_section');
      
      return `
    <script>
        // Scripts for ${item.title} (scoped to section-${index})
        ${hasShowTab ? modifiedScript : `(function() {\n${modifiedScript}\n})();`}
    </script>
    `;
    }).join('\n    ')}
</body>
</html>
`

      // Create and download the combined file
      const blob = new Blob([combinedHTML], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${projectId}_all_visualizations.html`
      link.click()
      URL.revokeObjectURL(url)
      
    } catch (error) {
      console.error('Failed to export visualizations:', error)
      alert('Failed to export visualizations. Please try again.')
    }
  }

  const getDiagramIcon = (type: string) => {
    switch (type) {
      case 'architecture': return <Network className="w-5 h-5" />
      case 'dataflow': return <GitBranch className="w-5 h-5" />
      case 'timeline': return <Calendar className="w-5 h-5" />
      case 'effort': return <BarChart3 className="w-5 h-5" />
      case 'risks': return <AlertTriangle className="w-5 h-5" />
      case 'techstack': return <Code2 className="w-5 h-5" />
      case 'team': return <Users className="w-5 h-5" />
      default: return <ImageIcon className="w-5 h-5" />
    }
  }

  const getDiagramTitle = (type: string) => {
    const titles: Record<string, string> = {
      architecture: 'System Architecture',
      dataflow: 'Data Flow Diagram',
      timeline: 'Project Timeline',
      effort: 'Effort Breakdown',
      risks: 'Risk Assessment',
      techstack: 'Technology Stack',
      team: 'Team Structure'
    }
    return titles[type] || type
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-7xl h-[90vh] flex flex-col">
        <CardHeader className="flex-shrink-0 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ImageIcon className="w-6 h-6 text-primary" />
              <CardTitle>Visualizations - {projectName || projectId}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={generateVisualizations}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
              {diagrams.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportAllVisualizations}
                  disabled={generating}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden p-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : diagrams.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <ImageIcon className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Visualizations Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                Generate professional diagrams and charts from your proposal to better communicate
                your technical solution and project plan.
              </p>
              <Button onClick={generateVisualizations} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Generate Visualizations
                  </>
                )}
              </Button>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="flex-shrink-0 w-full justify-start border-b rounded-none p-2 gap-1">
                <TabsTrigger value="overview" className="gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Overview
                </TabsTrigger>
                {diagrams.map((diagram) => (
                  <TabsTrigger key={diagram.type} value={diagram.type} className="gap-2">
                    {getDiagramIcon(diagram.type)}
                    {getDiagramTitle(diagram.type)}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex-1 overflow-auto">
                <TabsContent value="overview" className="p-6 space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Interactive Visualizations</h3>
                    <p className="text-muted-foreground">
                      Auto-generated diagrams and charts from your proposal
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {diagrams.map((diagram) => (
                      <Card
                        key={diagram.type}
                        className="cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => setActiveTab(diagram.type)}
                      >
                        <CardContent className="p-6 text-center">
                          <div className="flex justify-center mb-3">
                            {getDiagramIcon(diagram.type)}
                          </div>
                          <h4 className="font-semibold mb-2">{getDiagramTitle(diagram.type)}</h4>
                          <p className="text-sm text-muted-foreground">
                            {diagram.filename.endsWith('.html') ? 'Interactive' : 'Static'} diagram
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 Features</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Interactive Charts:</strong> Zoom, pan, and hover for details</li>
                      <li>• <strong>Professional Quality:</strong> Graphviz & Plotly powered</li>
                      <li>• <strong>Export Ready:</strong> Download as PNG for presentations</li>
                      <li>• <strong>Real-time Generation:</strong> Auto-updated from proposal data</li>
                    </ul>
                  </div>
                </TabsContent>

                {diagrams.map((diagram) => (
                  <TabsContent key={diagram.type} value={diagram.type} className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold flex items-center gap-2">
                        {getDiagramIcon(diagram.type)}
                        {getDiagramTitle(diagram.type)}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadDiagram(diagram)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>

                    <Card>
                      <CardContent className="p-4">
                        {(() => {
                          const loaded = loadedDiagrams[diagram.filename]
                          if (!loaded) {
                            void ensureDiagramLoaded(diagram)
                            return (
                              <div className="flex items-center justify-center h-[300px]">
                                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                              </div>
                            )
                          }

                          if (loaded.kind === 'html') {
                            return (
                              <iframe
                                srcDoc={loaded.html}
                                className="w-full h-[600px] border-0 rounded-lg"
                                title={getDiagramTitle(diagram.type)}
                              />
                            )
                          }

                          return (
                            <img
                              src={loaded.url}
                              alt={getDiagramTitle(diagram.type)}
                              className="w-full h-auto rounded-lg"
                            />
                          )
                        })()}
                      </CardContent>
                    </Card>

                    <div className="text-sm text-muted-foreground">
                      {diagram.filename.endsWith('.html') && (
                        <p>💡 <strong>Tip:</strong> Use the toolbar in the chart to zoom, pan, or download as PNG</p>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </div>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
